import React, { useContext, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { Grid, GridItem, Tooltip, Button } from '@patternfly/react-core';
import NavigationIcon from '@patternfly/react-icons/dist/esm/icons/location-arrow-icon';
import CheckIcon from '@patternfly/react-icons/dist/esm/icons/check-square-icon';
// import VideoIcon from '@patternfly/react-icons/dist/esm/icons/video-icon';
import PageIcon from '@patternfly/react-icons/dist/esm/icons/book-open-icon';
import { InfoCircleIcon, VideoIcon } from '@patternfly/react-icons';
import CountUp from 'react-countup';
import { Duration } from 'luxon';
import { useReactToPrint } from 'react-to-print';
import SVG from 'react-inlinesvg';
import { storesContext } from 'stores';
import ExpandableSection from 'components/ExpandableSection';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import PDFIcon from 'img/icons/pdf-download.svg';
import CourseIcon from 'img/icons/course_small.svg';
import LessonIcon from 'img/icons/lessonicon.svg';
import { useFeature } from '@ole-ui/ole-ui-components';
import { formatDateTime } from 'services/UtilityService';
import { requestOrganizationSummaryUpdate } from 'services/ManagementReportingService';

import { SummaryType } from 'types/organization';
import ActivityChartFrame from './ActivityChartFrame';
import TechnologyChartsFrame from './TechnologyChartsFrame';
import TrainingHoursChartFrame from './TrainingHoursChartFrame';
import CompletedCoursesChartFrame from './CompletedCoursesChartFrame';
import CompletedExamsChartFrame from './CompletedExamsChartFrame';
import CourseProgress from './CourseProgress';
import ManagerProgress from './ManagerProgress';

import './styles.scss';

const SummaryDashboard = () => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const openPrintDialog = useReactToPrint({
    content: () => componentRef.current,
  });
  const {
    userStore,
    examsStore,
    organizationStore,
    catalogStore,
    vocabularyStore,
  } = useContext(storesContext);
  const {
    organization,
    usageSummary,
    // kalturaVideosWatched,
    totalHoursTimeSeries,
    isTotalHoursTSLoading,
    courseCompletionTimeSeries,
    isCourseCompletionTSLoading,
    examCompletionTimeSeries,
    isExamCompletionTSLoading,
    summaryLastUpdate,
    isUsageSummaryLoading,
  } = organizationStore;

  const isTrainingHoursChartEnabled = useFeature({
    section: 'mgmt_overview',
    flagName: 'training_hours_chart',
    defaultFlagValue: false,
  });

  const isCompletedCoursesChartEnabled = useFeature({
    section: 'mgmt_overview',
    flagName: 'completed_courses_chart',
    defaultFlagValue: false,
  });

  const isCompletedExamsChartEnabled = useFeature({
    section: 'mgmt_overview',
    flagName: 'completed_exams_chart',
    defaultFlagValue: false,
  });

  const isLessonSummaryEnabled = useFeature({
    section: 'mgmt_overview',
    flagName: 'lesson_summary',
    defaultFlagValue: false,
  });

  const [productVocabulary, setProductVocabulary] = React.useState([]);
  const [topicVocabulary, setTopicVocabulary] = React.useState([]);

  React.useEffect(() => {
    const fetchVocabularies = async () => {
      try {
        const prodVoc = await vocabularyStore.getVocabularyByNamespace(
          'offering_taxonomy_products',
          i18n.language,
        );
        setProductVocabulary(prodVoc || []);
        const topicVoc = await vocabularyStore.getVocabularyByNamespace(
          'offering_taxonomy_topics',
          i18n.language,
        );
        setTopicVocabulary(topicVoc || []);
      } catch (e) {
        setProductVocabulary([]);
        setTopicVocabulary([]);
      }
    };
    fetchVocabularies();
  }, [i18n.language, vocabularyStore]);

  useEffect(() => {
    if (organization.id) {
      catalogStore.getAllCatalogEntries(false);
      try {
        organizationStore.getUsageSummary(
          i18n,
          vocabularyStore,
          undefined,
          isTrainingHoursChartEnabled,
          isCompletedCoursesChartEnabled,
          isCompletedExamsChartEnabled,
          isLessonSummaryEnabled,
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [organization.id, i18n, vocabularyStore]);

  const handlePrint = () => {
    if (!organizationStore.isUsageSummaryLoading) {
      openPrintDialog();
    }
  };

  useEffect(() => {
    if (organization.id) {
      clearInterval(organizationStore.summaryPollingId);
      if (organizationStore.summaryType !== SummaryType.Custom) {
        organizationStore.summaryPollingId = setInterval(
          organizationStore.getUsageSummary,
          60000,
          i18n,
          vocabularyStore,
          undefined,
          isTrainingHoursChartEnabled,
          isCompletedCoursesChartEnabled,
          isCompletedExamsChartEnabled,
          isLessonSummaryEnabled,
          false,
        );
      }
    }
    return () => {
      clearInterval(organizationStore.summaryPollingId);
    };
  }, [organization.id, i18n, vocabularyStore, organizationStore.summaryType]);

  const [isRefreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');

  useEffect(() => {
    if (lastUpdate !== summaryLastUpdate) {
      setRefreshing(false);
    }
    setLastUpdate(summaryLastUpdate);
  }, [summaryLastUpdate]);

  return (
    <>
      <div className="dashboard-summary-download-menu">
        {summaryLastUpdate &&
          organizationStore.summaryType !== SummaryType.Custom && (
            <>
              <div className="dashboard-summary-download-menu-updated">
                {t('Updated: ')}{' '}
                {formatDateTime(
                  summaryLastUpdate,
                  i18n.language,
                  'MMM DD YYYY, hh:mm A',
                )}
              </div>
              <Button
                id="refresh-summary-button"
                aria-label={t('Refresh summary dashboard data')}
                isLoading={isRefreshing}
                onClick={() => {
                  setRefreshing(true);
                  requestOrganizationSummaryUpdate(organization.id);
                }}
              >
                Refresh
              </Button>
            </>
          )}
        <Tooltip content={t('Download PDF')} entryDelay={0} exitDelay={0}>
          <span
            onClick={handlePrint}
            disabled={organizationStore.isUsageSummaryLoading}
            role="button"
            tabIndex="0"
            aria-label={t('Print summary as PDF')}
            className="dashboard-summary-download-menu-icon"
          >
            <SVG src={PDFIcon} title="Print summary as PDF" />
          </span>
        </Tooltip>
      </div>

      <div className="dashboard-wrapper" ref={componentRef}>
        <Grid hasGutter>
          {isLessonSummaryEnabled ? (
            <>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon card-course-icon">
                    <SVG
                      src={CourseIcon}
                      width={40}
                      height={40}
                      title={t('Course')}
                    />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.total_courses}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Courses Started').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon card-course-icon">
                    <CheckIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.completed_courses}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Courses Completed').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
                <div className="medium-card">
                  <div className="medium-card-data">
                    <div className="medium-card-info">
                      <div className="medium-card-display">
                        <div className="lab-hours-summary-wrapper">
                          <React.Fragment>
                            <span className="inline-count-up bold-data">
                              {usageSummary.average_completion_hours
                                ? Duration.fromObject({
                                    hours:
                                      usageSummary.average_completion_hours,
                                  }).toHuman()
                                : t('Not applicable')}
                            </span>
                          </React.Fragment>
                        </div>
                      </div>
                      <div className="medium-card-bottom">
                        {t('Average course completion').toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
                <div className="medium-card">
                  <div className="medium-card-data">
                    <div className="medium-card-info">
                      <div className="medium-card-display">
                        <div className="lab-hours-summary-wrapper">
                          <React.Fragment>
                            <span className="inline-count-up bold-data">
                              {usageSummary.average_lesson_completion_hours
                                ? Duration.fromObject({
                                    hours:
                                      usageSummary.average_lesson_completion_hours,
                                  }).toHuman()
                                : t('Not applicable')}
                            </span>
                          </React.Fragment>
                        </div>
                      </div>
                      <div className="medium-card-bottom">
                        {t('Average lesson completion').toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon card-lesson-icon">
                    <SVG
                      src={LessonIcon}
                      width={40}
                      height={40}
                      title={t('Lesson')}
                    />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.total_lessons}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Lessons Started').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon card-lesson-icon">
                    <CheckIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.completed_lessons}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Lessons Completed').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
            </>
          ) : (
            <>
              {' '}
              <GridItem span={12} lg={4} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon icon-1">
                    <NavigationIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.total_courses}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Courses Started').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={4} className="mini-card print-span-3">
                <div className="card-data">
                  <div className="card-icon icon-2">
                    {/* <Icon.CheckSquare /> */}
                    <CheckIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.completed_courses}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Courses Completed').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={4} className="mini-card print-span-3">
                <div className="card-data">
                  <Tooltip
                    aria-label="Basic tooltip"
                    content={t(
                      'Total number of live training sessions all users have attended',
                    )}
                    entryDelay={0}
                    exitDelay={0}
                  >
                    <Button
                      className="premium-info-button"
                      variant="plain"
                      icon={
                        <InfoCircleIcon color="var(--pf-v5-global--info-color--100)" />
                      }
                    />
                  </Tooltip>
                  <div className="card-icon icon-4">
                    <PageIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.attended_sessions}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Premium Sessions').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
            </>
          )}
          <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
            <div className="medium-card">
              <div className="medium-card-data">
                <div className="medium-card-info">
                  <div className="medium-card-display">
                    <div className="exam-data-wrapper">
                      {!examsStore?.exams ||
                      !usageSummary?.exams ||
                      isUsageSummaryLoading ? (
                        t('Loading content...')
                      ) : (
                        <React.Fragment>
                          <div className="exam-data-holder">
                            <span>{t('SCHEDULED').toUpperCase()}</span>
                            <span className="exam-data bold-data">
                              {`${
                                (usageSummary.exams.scheduled || 0) +
                                (usageSummary.exams.retake_scheduled || 0)
                              }`}
                            </span>
                          </div>
                          <div className="exam-data-holder">
                            <span>{t('UNSCHEDULED').toUpperCase()}</span>
                            <span className="exam-data bold-data">
                              {`${
                                (usageSummary.exams.unscheduled || 0) +
                                (usageSummary.exams.retake_available || 0)
                              }`}
                            </span>
                          </div>
                          <div className="exam-data-holder">
                            <span>{t('CANCELED').toUpperCase()}</span>
                            <span className="exam-data bold-data">
                              {`${usageSummary.exams.canceled || 0}`}
                            </span>
                          </div>
                          <div className="exam-data-holder">
                            <span>{t('COMPLETED').toUpperCase()}</span>
                            <span className="exam-data bold-data">
                              {`${
                                (usageSummary.exams.completed || 0) +
                                (usageSummary.exams.retake_complete || 0) +
                                (usageSummary.exams.retake_available || 0) +
                                (usageSummary.exams.retake_scheduled || 0) +
                                (usageSummary.exams.retake_results_pending ||
                                  0) +
                                (usageSummary.exams.results_pending || 0)
                              }`}
                            </span>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                  <div className="medium-card-bottom">
                    {t('Exams').toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
            <div className="medium-card">
              <div className="medium-card-data">
                <div className="medium-card-info">
                  <div className="medium-card-display">
                    <div className="lab-hours-summary-wrapper">
                      <React.Fragment>
                        <p className="giant-nr">
                          {`${Math.round(usageSummary.training_hours) || 0}`}
                        </p>
                      </React.Fragment>
                    </div>
                  </div>
                  <div className="medium-card-bottom">
                    {t('Training hours').toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
            <div className="medium-card">
              <div className="medium-card-data">
                <div className="medium-card-info">
                  <div className="medium-card-display">
                    <div className="lab-hours-summary-wrapper">
                      <React.Fragment>
                        <p className="giant-nr">
                          {`${Math.round(usageSummary.lab_hours) || 0}`}
                        </p>
                      </React.Fragment>
                    </div>
                  </div>
                  <div className="medium-card-bottom">
                    {t('Lab hours').toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </GridItem>
          {isLessonSummaryEnabled ? (
            <>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <Tooltip
                    aria-label="Basic tooltip"
                    content={t(
                      'Total number of live training sessions all users have attended',
                    )}
                    entryDelay={0}
                    exitDelay={0}
                  >
                    <Button
                      className="premium-info-button"
                      variant="plain"
                      icon={
                        <InfoCircleIcon color="var(--pf-v5-global--info-color--100)" />
                      }
                    />
                  </Tooltip>
                  <div className="card-icon icon-4">
                    <PageIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.attended_sessions}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Premium Sessions').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={12} lg={3} className="mini-card print-span-3">
                <div className="card-data">
                  <Tooltip
                    aria-label="Basic tooltip"
                    content={t(
                      'Total number of videos all users have started watching in the Learning Subscription',
                    )}
                    entryDelay={0}
                    exitDelay={0}
                  >
                    <Button
                      className="videos-info-button"
                      variant="plain"
                      icon={
                        <InfoCircleIcon color="var(--pf-v5-global--info-color--100)" />
                      }
                    />
                  </Tooltip>
                  <div className="card-icon icon-5">
                    <VideoIcon size="md" />
                  </div>
                  <div className="card-info">
                    {!userStore.usageSummary || isUsageSummaryLoading ? (
                      <p>{t('Loading content...')}</p>
                    ) : (
                      <React.Fragment>
                        <span className="bold-data">
                          <CountUp
                            start={0}
                            end={usageSummary.vc_courses}
                            duration={1}
                            preserveValue
                          />
                        </span>
                        <span>{t('Videos Watched').toUpperCase()}</span>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </GridItem>
            </>
          ) : (
            <GridItem span={12} lg={3} rowSpan={2} className="print-span-3">
              <div className="medium-card">
                <div className="medium-card-data">
                  <div className="medium-card-info">
                    <div className="medium-card-display">
                      <div className="lab-hours-summary-wrapper">
                        <React.Fragment>
                          <span className="inline-count-up bold-data">
                            {usageSummary.average_completion_hours
                              ? Duration.fromObject({
                                  hours: usageSummary.average_completion_hours,
                                }).toHuman()
                              : t('Not applicable')}
                          </span>
                        </React.Fragment>
                      </div>
                    </div>
                    <div className="medium-card-bottom">
                      {t('Average course completion').toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>
            </GridItem>
          )}
          <GridItem span={12} lg={6} className="print-span-6">
            <div className="medium-card">
              <div className="medium-card-data">
                <ActivityChartFrame
                  studentActivity={usageSummary.student_activity}
                />
              </div>
            </div>
          </GridItem>
          <GridItem span={12} lg={6} className="print-span-6">
            <div className="medium-card">
              <div className="medium-card-data">
                <TechnologyChartsFrame
                  products={usageSummary.products}
                  topics={usageSummary.topics}
                  productVocabulary={productVocabulary}
                  topicVocabulary={topicVocabulary}
                />
              </div>
            </div>
          </GridItem>
          {isTrainingHoursChartEnabled && (
            <GridItem span={12} lg={12} className="print-span-12">
              <div className="medium-card">
                <div className="medium-card-data">
                  <TrainingHoursChartFrame
                    labHours={totalHoursTimeSeries?.lab_hours}
                    trainingHours={totalHoursTimeSeries?.training_hours}
                    isLoading={isTotalHoursTSLoading || isUsageSummaryLoading}
                  />
                </div>
              </div>
            </GridItem>
          )}
          {isCompletedCoursesChartEnabled && (
            <GridItem span={12} lg={12} className="print-span-12">
              <div className="medium-card">
                <div className="medium-card-data">
                  <CompletedCoursesChartFrame
                    completedCourses={
                      courseCompletionTimeSeries?.completed_courses
                    }
                    isLoading={
                      isCourseCompletionTSLoading || isUsageSummaryLoading
                    }
                  />
                </div>
              </div>
            </GridItem>
          )}
          {isCompletedExamsChartEnabled && (
            <GridItem span={12} lg={12} className="print-span-12">
              <div className="medium-card">
                <div className="medium-card-data">
                  <CompletedExamsChartFrame
                    completedExams={examCompletionTimeSeries}
                    isLoading={
                      isExamCompletionTSLoading || isUsageSummaryLoading
                    }
                  />
                </div>
              </div>
            </GridItem>
          )}
        </Grid>

        <div className="medium-card mt-16">
          <div className="medium-card-data">
            <ExpandableSection
              localStorageStateKey="courseProgressTableCollapseState"
              title={t('Overall course progress')}
              color="light"
            >
              <CourseProgress
                isLoading={isUsageSummaryLoading}
                progressPerCourse={
                  organizationStore.progressPerCoursesWithNames
                }
                setFilters={organizationStore.setProgressPerCourseFilters}
                setTextFilter={organizationStore.setProgressPerCourseTextFilter}
              />
            </ExpandableSection>
          </div>
        </div>

        {organizationStore.summaryType === SummaryType.Current &&
          organizationStore.isAdminOfCurrentlySelectedOrg && (
            <div className="medium-card mt-16">
              <div className="medium-card-data">
                <ExpandableSection
                  localStorageStateKey="managerProgressTableCollapseState"
                  title={t('Managers')}
                  color="light"
                >
                  <ManagerProgress
                    isLoading={isUsageSummaryLoading}
                    data={organizationStore.filteredManagerProgress}
                    filterValue={organizationStore.managerProgressFilter}
                    onFilterChange={(value) =>
                      organizationStore.setManagerProgressFilter(value)
                    }
                  />
                </ExpandableSection>
              </div>
            </div>
          )}
        <div className="dashboard-wrapper__print-width-guarantee" />
      </div>
    </>
  );
};

export default withErrorBoundary(
  observer(SummaryDashboard),
  ComponentError,
  handleError,
);
